import { useEffect, useRef, useState } from 'react';
import { Button, CERTIFICATE_REFERENCE_WIDTH, certificateFont, fitFontSize } from '@mlm/ui';
import { certificateImageUrl, type CertificateRender } from '../lib/api';
import { CertificateShare } from './certificate-share';
import {
  base64FromDataUrl,
  canSaveFileNative,
  isNativeShell,
  saveFileNative,
} from '@mlm/ui/lib/native-shell';

/**
 * Renders an issued certificate client-side: the template image + the
 * snapshotted placeholder layout (member name, course title, date, verify
 * code) drawn onto a canvas, downloadable as PNG. No server raster — the
 * verification code + `/certificates/verify/:code` is the credential.
 */
export function CertificateCanvas({ cert }: { cert: CertificateRender }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Native only: the rendered PNG, shown for a long-press save. */
  const [saveImage, setSaveImage] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setReady(false);
    setFailed(false);
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      const values: Record<string, string> = {
        member_name: cert.memberName,
        course_title: cert.courseTitle,
        issued_date: new Date(cert.issuedAt).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        verify_code: cert.verificationCode,
      };
      for (const p of cert.config.placeholders) {
        const text = values[p.field] ?? '';
        const x = (p.xPct / 100) * canvas.width;
        const y = (p.yPct / 100) * canvas.height;
        const maxW = (p.widthPct / 100) * canvas.width;
        ctx.fillStyle = p.color;
        ctx.textAlign = p.align;
        ctx.textBaseline = 'middle';
        // Font size is authored against a 1000px-wide reference; shrink to fit.
        // The same helper the template editor's preview measures with, so what
        // a creator places is what a learner receives.
        const size = fitFontSize(
          (font, t) => {
            ctx.font = font;
            return ctx.measureText(t).width;
          },
          text,
          p.fontSizePx * (canvas.width / CERTIFICATE_REFERENCE_WIDTH),
          maxW,
        );
        ctx.font = certificateFont(size);
        ctx.fillText(text, x, y, maxW);
      }
      setReady(true);
    };
    // Two sources, in order: the API path (same host the SPA already talks to,
    // so no R2 bucket CORS is involved) and then the presigned R2 URL, which is
    // all this had before. A misconfigured box still renders.
    let triedFallback = false;
    img.onerror = () => {
      if (!triedFallback && cert.imageUrl) {
        triedFallback = true;
        img.src = cert.imageUrl;
        return;
      }
      setFailed(true);
    };

    // Drawing before the webfont resolves rasterizes the certificate in a
    // fallback face — wrong, and permanently so once downloaded. Wait, but
    // never indefinitely: a WebView whose `fonts.load` never settles must still
    // produce a certificate.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    const fontReady = fonts?.load
      ? Promise.race([fonts.load(certificateFont(48)), new Promise((r) => setTimeout(r, 1500))])
      : Promise.resolve();
    void fontReady.then(
      () => {
        if (!cancelled) img.src = certificateImageUrl(cert.verificationCode);
      },
      () => {
        if (!cancelled) img.src = certificateImageUrl(cert.verificationCode);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [cert]);

  async function download() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const png = canvas.toDataURL('image/png');
    const filename = `certificate-${cert.verificationCode}.png`;

    // In the Capacitor WebView an `<a download>` on a data: URL does nothing at
    // all, so the app needs the file plugins. Phase 9 ships them.
    if (canSaveFileNative()) {
      const base64 = base64FromDataUrl(png);
      if (base64 && (await saveFileNative({ filename, base64, title: 'Certificate' }))) return;
    }
    // Still inside the app but without the plugins — an APK built before they
    // were added. Long-press → "Save image" is the gesture that works there;
    // falling through to the anchor would be a silent no-op.
    if (isNativeShell()) {
      setSaveImage(png);
      return;
    }
    const a = document.createElement('a');
    a.download = filename;
    a.href = png;
    a.click();
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border bg-muted/30">
        <canvas ref={canvasRef} className="block h-auto w-full" />
        {failed && (
          <p className="p-4 text-sm text-destructive">
            The certificate image could not be loaded. Your certificate is still valid — code{' '}
            <span className="font-mono">{cert.verificationCode}</span>.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">
          Verification code <span className="font-mono font-semibold">{cert.verificationCode}</span>
        </span>
        <Button size="sm" onClick={() => void download()} disabled={!ready}>
          {isNativeShell() ? 'Save certificate' : 'Download PNG'}
        </Button>
      </div>
      <CertificateShare
        code={cert.verificationCode}
        courseTitle={cert.courseTitle}
        issuedAt={cert.issuedAt}
      />
      {saveImage && (
        <div className="space-y-2 rounded-lg border bg-card p-3">
          <p className="text-sm font-medium">Press and hold the image, then choose “Save image”.</p>
          <img
            src={saveImage}
            alt={`Certificate ${cert.verificationCode}`}
            className="block h-auto w-full rounded"
          />
          <Button size="sm" variant="outline" onClick={() => setSaveImage(null)}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
